import { createContext, useEffect, useState } from 'react';

import {
  addIdsToMenu,
  setActiveMenuRoutes,
  isDeepActive
} from './SideNav.helpers.js';
import useIsMobile from '../../hooks/useIsMobile.js';

const getNewMenu = (itemId, navMenu) => {
  // Used to store an item we removed active from, so that we can later find its parent and set
  // that item to active
  let unsetActiveItemId;

  const setActiveRecursive = (menu, activeId) => (
    menu.map((item) => {
      const processedItem = { ...item };
      if (activeId === item.id) {
        if (('subMenu' in item) && isDeepActive(item.subMenu)) {
          // Clicked on a parent with a deep child active, so we just collapse it all
          processedItem.active = false;
          unsetActiveItemId = item.id;
        } else if (item.active) {
          // Clicked on an already active item, so we set it to not active and note it
          processedItem.active = false;
          unsetActiveItemId = item.id;
        } else {
          // Clicked on a non-active item, so set it to active
          processedItem.active = true;
        }
      } else {
        // Not a match, make sure it is not active
        processedItem.active = false;
      }

      // Search through all descendants
      if ('subMenu' in item) {
        processedItem.subMenu = setActiveRecursive(item.subMenu, activeId);
      }

      return processedItem;
    })
  );

  let newMenu = setActiveRecursive(navMenu, itemId);

  // If we unset an item from active, we need to check and see if there's a parent we can set
  // as active.
  if (unsetActiveItemId && unsetActiveItemId.length > 1) {
    newMenu = setActiveRecursive(newMenu, unsetActiveItemId.slice(0, -2));
  }

  return newMenu;
};

export const SideNavContext = createContext();

export function SideNavContextProvider({
  menu,
  currentRoute,
  showMobileMenu,
  setShowMobileMenu,
  children,
}) {
  const isMobile = useIsMobile(992);
  const [navMenu, setNavMenu] = useState(addIdsToMenu(menu));
  // Side nav placeholder element styles used in mobile to correctly pad out space
  const [placeholderStyles, setPlaceholderStyles] = useState({});
  /* Overflow for the scroll container is scroll in desktop
    In mobile when opening a sub menu this gets set to hidden so the sub menu is full screen.
    this gets set
  */
  const [scrollContainerOverflow, setScrollContainerOverflow] = useState('scroll');

  useEffect(() => {
    const newMenu = addIdsToMenu(menu);
    const processedMenu = setActiveMenuRoutes(newMenu, currentRoute);
    setNavMenu(processedMenu);
    // close mobile menu when route changes
    setShowMobileMenu(false);
  }, [menu, currentRoute]);

  const setActive = (itemId) => {
    const newMenu = getNewMenu(itemId, navMenu);

    setNavMenu(newMenu);
  };

  return (
    <SideNavContext.Provider
      value={{
        navMenu,
        setActive,
        currentRoute,
        placeholderStyles,
        setPlaceholderStyles,
        scrollContainerOverflow,
        setScrollContainerOverflow,
        isMobile,
        showMobileMenu,
      }}
    >
      {children}
    </SideNavContext.Provider>
  );
}
